import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { INSPECTION_ROUNDS } from '../../../data/nurseAppModels';

const MyInspectionsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all'); // all, DRAFT, PENDING_APPROVAL, APPROVED, REJECTED

  useEffect(() => {
    const loadInspections = async () => {
      if (!currentUser || !currentTeam) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let inspectionsQuery = query(
          collection(db, 'equipmentInspections'),
          where('teamId', '==', currentTeam.id),
          where('inspectedBy', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(inspectionsQuery);
        const inspectionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setInspections(inspectionsData);
      } catch (err) {
        console.error('Error loading inspections:', err);
        // Fallback: query without orderBy if index doesn't exist
        try {
          const fallbackQuery = query(
            collection(db, 'equipmentInspections'),
            where('teamId', '==', currentTeam.id),
            where('inspectedBy', '==', currentUser.uid)
          );
          const snapshot = await getDocs(fallbackQuery);
          const inspectionsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort in memory
          inspectionsData.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA;
          });
          setInspections(inspectionsData);
        } catch (fallbackErr) {
          console.error('Error loading inspections (fallback):', fallbackErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadInspections();
  }, [currentUser, currentTeam]);

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800', label: '📝 Draft', icon: '📝' },
      PENDING_APPROVAL: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '⏳ รออนุมัติ', icon: '⏳' },
      APPROVED: { bg: 'bg-green-100', text: 'text-green-800', label: '✅ อนุมัติแล้ว', icon: '✅' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-800', label: '❌ ไม่อนุมัติ', icon: '❌' }
    };
    return badges[status] || badges.DRAFT;
  };

  const getRoundLabel = (round) => {
    const rounds = {
      [INSPECTION_ROUNDS.MORNING_BEFORE]: 'เช้า - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.MORNING_AFTER]: 'เช้า - หลังเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_BEFORE]: 'บ่าย - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_AFTER]: 'บ่าย - หลังเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_BEFORE]: 'ดึก - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_AFTER]: 'ดึก - หลังเข้าเวร'
    };
    return rounds[round] || round;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredInspections = filterStatus === 'all' 
    ? inspections 
    : inspections.filter(inspection => inspection.status === filterStatus);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
          >
            ← กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800">รายการตรวจสอบของฉัน</h1>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterStatus === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ทั้งหมด ({inspections.length})
            </button>
            <button
              onClick={() => setFilterStatus('DRAFT')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterStatus === 'DRAFT'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Draft ({inspections.filter(i => i.status === 'DRAFT').length})
            </button>
            <button
              onClick={() => setFilterStatus('PENDING_APPROVAL')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterStatus === 'PENDING_APPROVAL'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              รออนุมัติ ({inspections.filter(i => i.status === 'PENDING_APPROVAL').length})
            </button>
            <button
              onClick={() => setFilterStatus('APPROVED')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterStatus === 'APPROVED'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              อนุมัติแล้ว ({inspections.filter(i => i.status === 'APPROVED').length})
            </button>
            <button
              onClick={() => setFilterStatus('REJECTED')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterStatus === 'REJECTED'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ไม่อนุมัติ ({inspections.filter(i => i.status === 'REJECTED').length})
            </button>
          </div>
        </div>

        {/* Inspections List */}
        {filteredInspections.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {filterStatus === 'all' ? 'ยังไม่มีรายการตรวจสอบ' : `ไม่มีรายการสถานะ ${filterStatus}`}
            </h3>
            <p className="text-gray-600 mb-6">
              {filterStatus === 'all' 
                ? 'เริ่มต้นการตรวจสอบอุปกรณ์ใหม่ได้เลย' 
                : 'ลองเลือกสถานะอื่นดู'}
            </p>
            {filterStatus === 'all' && (
              <Link
                to="/nurse/equipment/inspection"
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                + สร้างการตรวจสอบใหม่
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredInspections.map((inspection) => {
              const statusBadge = getStatusBadge(inspection.status);
              return (
                <div
                  key={inspection.id}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                          {statusBadge.label}
                        </span>
                        <span className="text-sm text-gray-600">
                          {getRoundLabel(inspection.round)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">วันที่ตรวจสอบ</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formatDate(inspection.date)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">จำนวนรายการ</p>
                          <p className="text-sm font-medium text-gray-800">
                            {inspection.totalItems || 0} รายการ
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">วันที่สร้าง</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formatDate(inspection.createdAt)}
                          </p>
                        </div>
                      </div>

                      {inspection.status === 'REJECTED' && inspection.rejectionReason && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-xs text-red-600 font-semibold mb-1">เหตุผลที่ไม่อนุมัติ:</p>
                          <p className="text-sm text-red-800">{inspection.rejectionReason}</p>
                        </div>
                      )}

                      {inspection.status === 'APPROVED' && inspection.approvedAt && (
                        <div className="mt-4 text-sm text-gray-600">
                          <p>อนุมัติโดย: {inspection.approvedByName || '-'}</p>
                          <p>วันที่อนุมัติ: {formatDate(inspection.approvedAt)}</p>
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex flex-col gap-2">
                      {inspection.status === 'APPROVED' ? (
                        <Link
                          to={`/nurse/equipment/inspections/${inspection.id}/review`}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                        >
                          ดูรายละเอียด
                        </Link>
                      ) : (
                        <>
                          <Link
                            to={`/nurse/equipment/inspection/${inspection.id}`}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                          >
                            {inspection.status === 'REJECTED' ? 'แก้ไข' : 'แก้ไข'}
                          </Link>
                          {inspection.status === 'REJECTED' && (
                            <button
                              onClick={() => navigate(`/nurse/equipment/inspection/${inspection.id}`)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                            >
                              แก้ไขและส่งใหม่
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyInspectionsPage;

